import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile/use-patient-profile';
import { ContactApi, ContactSearchByNumber } from '../../../../../../services/api-client/contact-api';
import LocationService from '../../../../../../services/location-service/location-service';
import {
  EventInboxCommunicationsWidget,
  EventPatientProfile,
  EventPatientProfileWidgetNavigation,
  EventPatientProfileWithPhoneNumber
} from '../../../../../../types/event-bus';
import { importantContacts } from '../../../../../../utils/contact';
import { WidgetRoutePath } from '../../navigation/route-config';
import { BreadcrumbExtensionProps, PatientProfileWidgetContext } from './context';

export interface PatientProfileWidgetProviderProps {
  children: ReactNode;
  patientIdEvent: EventPatientProfile | undefined;
  patientPhoneEvent: EventPatientProfileWithPhoneNumber | undefined;
  patientInboxEvent: EventInboxCommunicationsWidget | undefined;
}

export const PatientProfileWidgetProvider: FC<PatientProfileWidgetProviderProps> = (props) => {
  const { patientIdEvent, patientPhoneEvent, patientInboxEvent } = props;
  const { config } = useConfig();
  const { publish } = useEventBus();
  const { oidcService } = useOidcWithAdmin();
  const { patientId, loadPatientDataById, contactDetails, contactLocationInfo, patientInfo } = usePatientProfile();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [route, setRoute] = useState<WidgetRoutePath>();
  const [locationId, setLocationId] = useState<number>(0);
  const [contactsByPhone, setContactsByPhone] = useState<ContactSearchByNumber[]>(null!);
  const [isSkinnyProfile, setIsSkinnyProfile] = useState<boolean>(false);
  const [breadcrumbExtensions, setBreadcrumbExtensions] = useState<BreadcrumbExtensionProps>();
  const locationApi = useMemo(() => LocationService.getInstance(config, oidcService), [config, oidcService]);
  const contactApi = ContactApi.getInstance();

  useEffect(() => {
    (async () => {
      if (!patientIdEvent && !patientPhoneEvent && !patientInboxEvent) return;

      setIsLoading(true);

      if (patientIdEvent) {
        await loadPatientDataById(patientIdEvent.data.patientId);
        setRoute(patientIdEvent.data.route ? patientIdEvent.data.route : WidgetRoutePath.MAIN);
      } else if (patientPhoneEvent) {
        setRoute(WidgetRoutePath.MAIN);
        await loadPatientDataByPhone(patientPhoneEvent.data.phoneNumber, patientPhoneEvent.data.locationId);
      } else if (patientInboxEvent) {
        if (patientInboxEvent.data.patientId) await loadPatientDataById(patientInboxEvent.data.patientId);
        else if (patientInboxEvent.data.phoneNumber && patientInboxEvent.data.locationId)
          await loadPatientDataByPhone(patientInboxEvent.data.phoneNumber, patientInboxEvent.data.locationId);

        setRoute(WidgetRoutePath.INBOX);
      }

      setIsLoading(false);
    })();
  }, [patientIdEvent, patientPhoneEvent, patientInboxEvent]);

  useEffect(() => {
    contactDetails &&
    patientInfo &&
    locationId &&
    contactLocationInfo &&
    !contactLocationInfo.map((location) => `${location.locationId}`).includes(`${locationId}`)
      ? setIsSkinnyProfile(true)
      : setIsSkinnyProfile(false);
  }, [contactDetails, patientInfo, locationId, isSkinnyProfile, contactLocationInfo, setIsSkinnyProfile]);

  const loadPatientDataByPhone = async (phoneNumber: string, locationId: number) => {
    try {
      const contactsByPhone = await contactApi.getContactsByPhoneNumbers(phoneNumber);
      const location = await locationApi.getLocation(locationId);

      setLocationId(location.id);

      if (contactsByPhone[phoneNumber].length === 1) {
        await loadPatientDataById(contactsByPhone[phoneNumber][0].id);
      } else {
        const finalContacts = importantContacts(contactsByPhone[phoneNumber], patientId, location.idNetwork);

        setContactsByPhone(finalContacts);

        if (patientInboxEvent) {
          await loadPatientDataById(finalContacts[0].id);
        } else {
          setRoute(WidgetRoutePath.PROFILE_SELECTOR);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const goTo = (newRoute: WidgetRoutePath, skipEventBus?: boolean) => {
    if (newRoute === route) return;

    if (!skipEventBus) {
      publish({
        messageType: 'patient-profile-widget-navigation',
        data: {
          route: newRoute
        } as EventPatientProfileWidgetNavigation['data']
      });
    }

    setRoute(newRoute);
  };

  const contextValue = useMemo(
    () => ({
      patientIdEvent,
      patientPhoneEvent,
      patientInboxEvent,
      route,
      goTo,
      isLoading,
      isSkinnyProfile,
      breadcrumbExtensions,
      setBreadcrumbExtensions,
      contactsByPhone
    }),
    [
      patientIdEvent,
      patientPhoneEvent,
      patientInboxEvent,
      route,
      goTo,
      isLoading,
      isSkinnyProfile,
      breadcrumbExtensions,
      setBreadcrumbExtensions,
      contactsByPhone
    ]
  );

  return (
    <PatientProfileWidgetContext.Provider value={contextValue}>{props.children}</PatientProfileWidgetContext.Provider>
  );
};
